// ============================================
// 3. TradeAnalysis.js - Individual Trade Deep Dive
// ============================================

import React, { useState } from 'react';

const TradeAnalysis = () => {
  const [selectedTradeId, setSelectedTradeId] = useState(1);
  const [analysisView, setAnalysisView] = useState('execution');

  // Sample trade data with detailed analysis
  const tradeDatabase = {
    1: {
      ticker: 'AAPL',
      entryDate: '2025-07-15',
      exitDate: '2025-07-22',
      entryPrice: 175.50,
      exitPrice: 183.25,
      shares: 100,
      pnl: 775.00,
      pnlPercent: 4.42,
      strategy: 'Options Core',
      aiScore: 8.5,
      analysis: {
        catalyst: {
          primary: 'iPhone 16 Leak + Services Growth',
          strength: 'High',
          timing: 'Excellent',
          marketReaction: 'Strong positive response in first hour',
          competitorImpact: 'Samsung delayed announcement helped'
        },
        entry: {
          quality: 9,
          reasoning: 'Perfect technical setup with fundamental catalyst',
          marketCondition: 'Bullish momentum, low VIX (14.2)',
          volumeConfirmation: 'Above average by 180%',
          timing: 'Entered within 30 minutes of catalyst',
          priceAction: 'Clean breakout above $174 resistance'
        },
        riskManagement: {
          positionSize: '2.1% of portfolio - within 2.5% limit',
          stopLoss: 'Set at $170 (-3.1%) - properly executed',
          riskReward: '1:2.3 ratio - excellent setup',
          portfolioHeat: '8.2% total exposure - safe level'
        },
        execution: {
          entrySlippage: 0.02,
          exitSlippage: 0.01,
          timing: 'Excellent - avoided earnings volatility',
          fillQuality: 'Perfect fills on both entry and exit',
          platformPerformance: 'IBKR performed flawlessly'
        },
        exit: {
          method: 'Target hit at $183 resistance level',
          timing: 'Perfect - day before earnings announcement',
          reasoningQuality: 'Excellent risk management',
          emotionalControl: 'No FOMO, stuck to plan',
          improvement: 'None - textbook execution'
        },
        psychology: {
          entryEmotion: 'Confident but not overconfident',
          holdingPeriod: 'Comfortable throughout',
          exitEmotion: 'Satisfied with disciplined exit',
          stressLevel: 'Low - good position sizing',
          faithPrinciple: 'Toughness - stuck to exit plan despite FOMO'
        },
        lessons: {
          whatWorked: [
            'Catalyst timing was perfect',
            'Position sizing allowed for comfort',
            'Exit before earnings prevented volatility',
            'Technical levels held perfectly'
          ],
          whatDidntWork: [
            'Could have sized slightly larger given strength',
            'Exit was slightly early - stock went to $186'
          ],
          improvements: [
            'Consider scaling out in thirds vs all at once',
            'Watch earnings calendar more closely for exit timing',
            'Consider keeping small position through earnings'
          ],
          keyTakeaways: [
            'Strong catalysts + good technicals = high probability',
            'Position sizing is key to emotional control',
            'Taking profits before events is usually right'
          ]
        }
      }
    },
    2: {
      ticker: 'MSFT',
      entryDate: '2025-07-10',
      exitDate: '2025-07-18',
      entryPrice: 335.80,
      exitPrice: 328.45,
      shares: 50,
      pnl: -367.50,
      pnlPercent: -2.19,
      strategy: 'Government Intel',
      aiScore: 7.8,
      analysis: {
        catalyst: {
          primary: 'Cloud Security Contract Announcement',
          strength: 'Medium',
          timing: 'Good but overtaken by market',
          marketReaction: 'Initial positive, then broader selloff',
          competitorImpact: 'Minimal - company-specific news'
        },
        entry: {
          quality: 7,
          reasoning: 'Good catalyst but market timing was poor',
          marketCondition: 'Tech rotation beginning (hindsight)',
          volumeConfirmation: 'Average volume - no conviction',
          timing: 'Good relative to news, bad relative to market',
          priceAction: 'Broke above $335 but no follow-through'
        },
        riskManagement: {
          positionSize: '1.8% of portfolio - good sizing',
          stopLoss: 'Set at $325 (-3.2%) - properly executed',
          riskReward: '1:1.8 ratio - adequate but not great',
          portfolioHeat: '12.1% total exposure - at limit'
        },
        execution: {
          entrySlippage: 0.05,
          exitSlippage: 0.03,
          timing: 'Entry good, exit forced by stop',
          fillQuality: 'Good fills, minor slippage',
          platformPerformance: 'No issues'
        },
        exit: {
          method: 'Stop loss triggered at $328.45',
          timing: 'Automatic - good discipline',
          reasoningQuality: 'Excellent - followed rules',
          emotionalControl: 'Perfect - no revenge trading',
          improvement: 'None - proper risk management'
        },
        psychology: {
          entryEmotion: 'Confident in catalyst',
          holdingPeriod: 'Increasingly uncomfortable as tech sold off',
          exitEmotion: 'Disappointed but not angry',
          stressLevel: 'Medium - concerned about portfolio heat',
          faithPrinciple: 'Accountability - admitted mistake, followed rules'
        },
        lessons: {
          whatWorked: [
            'Stop loss discipline was perfect',
            'Recognized broader market issues',
            'Didn\'t revenge trade after loss',
            'Position sizing limited damage'
          ],
          whatDidntWork: [
            'Ignored broader tech weakness signals',
            'Catalyst wasn\'t strong enough for headwinds',
            'Portfolio heat was too high for risky trade'
          ],
          improvements: [
            'Watch sector rotation more carefully',
            'Reduce position size when portfolio heat >10%',
            'Wait for stronger catalysts in weak markets',
            'Add VIX/market condition filter'
          ],
          keyTakeaways: [
            'Company-specific catalysts don\'t beat market rotation',
            'Good risk management saves capital for better setups',
            'Portfolio heat management is crucial'
          ]
        }
      }
    }
  };

  const currentTrade = tradeDatabase[selectedTradeId];

  // Helper Functions
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);

  const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;

  const getScoreColor = (score) => {
    if (score >= 8) return '#00FF5F';
    if (score >= 6) return '#FFB800';
    return '#FF0000';
  };

  const renderAnalysisSection = () => {
    switch(analysisView) {
      case 'execution':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Catalyst Analysis */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                📈 Catalyst Analysis
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Primary Catalyst</div>
                  <div style={{ color: '#ffffff', fontSize: '16px', fontFamily: 'Georgia, serif' }}>
                    {currentTrade.analysis.catalyst.primary}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Strength</div>
                  <div style={{
                    color: currentTrade.analysis.catalyst.strength === 'High' ? '#00FF5F' : 
                          currentTrade.analysis.catalyst.strength === 'Medium' ? '#FFB800' : '#FF0000',
                    fontSize: '16px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {currentTrade.analysis.catalyst.strength}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Market Reaction</div>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                    {currentTrade.analysis.catalyst.marketReaction}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Timing</div>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                    {currentTrade.analysis.catalyst.timing}
                  </div>
                </div>
              </div>
            </div>

            {/* Entry Quality */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                🎯 Entry Analysis
              </h3>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Entry Quality Score</div>
                <div style={{
                  color: getScoreColor(currentTrade.analysis.entry.quality),
                  fontSize: '24px',
                  fontWeight: 'bold',
                  fontFamily: 'Georgia, serif'
                }}>
                  {currentTrade.analysis.entry.quality}/10
                </div>
              </div>
              <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif', lineHeight: '1.5' }}>
                <strong>Reasoning:</strong> {currentTrade.analysis.entry.reasoning}<br/>
                <strong>Market Condition:</strong> {currentTrade.analysis.entry.marketCondition}<br/>
                <strong>Volume:</strong> {currentTrade.analysis.entry.volumeConfirmation}<br/>
                <strong>Price Action:</strong> {currentTrade.analysis.entry.priceAction}
              </div>
            </div>

            {/* Risk Management */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                🛡️ Risk Management
              </h3>
              <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                <div><strong>Position Size:</strong> {currentTrade.analysis.riskManagement.positionSize}</div>
                <div><strong>Stop Loss:</strong> {currentTrade.analysis.riskManagement.stopLoss}</div>
                <div><strong>Risk/Reward:</strong> {currentTrade.analysis.riskManagement.riskReward}</div>
                <div><strong>Portfolio Heat:</strong> {currentTrade.analysis.riskManagement.portfolioHeat}</div>
              </div>
            </div>

            {/* Exit Analysis */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                🚪 Exit Analysis
              </h3>
              <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                <div><strong>Method:</strong> {currentTrade.analysis.exit.method}</div>
                <div><strong>Timing:</strong> {currentTrade.analysis.exit.timing}</div>
                <div><strong>Reasoning Quality:</strong> {currentTrade.analysis.exit.reasoningQuality}</div>
                <div><strong>Emotional Control:</strong> {currentTrade.analysis.exit.emotionalControl}</div>
              </div>
            </div>
          </div>
        );

      case 'psychology':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Psychology Assessment */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                🧠 Psychological Analysis
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Entry Emotion</div>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                    {currentTrade.analysis.psychology.entryEmotion}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Holding Period</div>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                    {currentTrade.analysis.psychology.holdingPeriod}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Exit Emotion</div>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                    {currentTrade.analysis.psychology.exitEmotion}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Stress Level</div>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                    {currentTrade.analysis.psychology.stressLevel}
                  </div>
                </div>
              </div>
            </div>

            {/* FAITH Principle */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                ✝️ FAITH Principle Applied
              </h3>
              <div style={{
                backgroundColor: '#1a2332',
                padding: '16px',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'Georgia, serif',
                lineHeight: '1.5'
              }}>
                {currentTrade.analysis.psychology.faithPrinciple}
              </div>
            </div>
          </div>
        );

      case 'lessons':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* What Worked */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#00FF5F', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                ✅ What Worked Well
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {currentTrade.analysis.lessons.whatWorked.map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#1a2332',
                    padding: '12px',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    • {item}
                  </div>
                ))}
              </div>
            </div>

            {/* What Didn't Work */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#FF0000', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                ❌ What Didn't Work
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {currentTrade.analysis.lessons.whatDidntWork.map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#1a2332',
                    padding: '12px',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    • {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Improvements */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#FFB800', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                🔧 Areas for Improvement
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {currentTrade.analysis.lessons.improvements.map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#1a2332',
                    padding: '12px',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    • {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Key Takeaways */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b'
            }}>
              <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
                💡 Key Takeaways
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {currentTrade.analysis.lessons.keyTakeaways.map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#1a2332',
                    padding: '12px',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'Georgia, serif',
                    fontWeight: '600'
                  }}>
                    🔸 {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      backgroundColor: '#1a2332',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      padding: '24px'
    }}>
      {/* WATERMARK */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0.03',
        pointerEvents: 'none',
        zIndex: '1',
        width: '800px',
        height: '800px'
      }}>
        <img 
          src="/finance-watermark.jpg"
          alt="Financial Trading Watermark"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>

      <div style={{ position: 'relative', zIndex: '2', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              color: '#ffffff',
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0',
              fontFamily: 'Georgia, serif'
            }}>🔬 Trade Analysis</h1>
            <p style={{
              color: '#a0a9c1',
              fontSize: '16px',
              margin: '8px 0 0 0',
              fontFamily: 'Georgia, serif'
            }}>Deep dive analysis of individual trades</p>
          </div>
        </div>

        {/* Trade Selector */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '16px' }}>
            <div>
              <label style={{ color: '#a0a9c1', fontSize: '14px', marginRight: '8px', fontFamily: 'Georgia, serif' }}>
                Select Trade:
              </label>
              <select 
                value={selectedTradeId}
                onChange={(e) => setSelectedTradeId(parseInt(e.target.value))}
                style={{
                  backgroundColor: '#1a2332',
                  color: '#ffffff',
                  border: '1px solid #3a4a6b',
                  borderRadius: '4px',
                  padding: '8px',
                  fontFamily: 'Georgia, serif'
                }}
              >
                <option value={1}>AAPL - Win (+4.42%)</option>
                <option value={2}>MSFT - Loss (-2.19%)</option>
              </select>
            </div>
          </div>

          {/* Trade Summary */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
            backgroundColor: '#1a2332',
            padding: '16px',
            borderRadius: '8px'
          }}>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Ticker</div>
              <div style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
                {currentTrade.ticker}
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>P&L</div>
              <div style={{
                color: currentTrade.pnl > 0 ? '#00FF5F' : '#FF0000',
                fontSize: '18px',
                fontWeight: 'bold',
                fontFamily: 'Georgia, serif'
              }}>
                {formatCurrency(currentTrade.pnl)}
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Return</div>
              <div style={{
                color: currentTrade.pnl > 0 ? '#00FF5F' : '#FF0000',
                fontSize: '18px',
                fontWeight: 'bold',
                fontFamily: 'Georgia, serif'
              }}>
                {formatPercent(currentTrade.pnlPercent)}
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Strategy</div>
              <div style={{ color: '#5BC0DE', fontSize: '16px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
                {currentTrade.strategy}
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>AI Score</div>
              <div style={{
                color: getScoreColor(currentTrade.aiScore),
                fontSize: '18px',
                fontWeight: 'bold',
                fontFamily: 'Georgia, serif'
              }}>
                {currentTrade.aiScore}/10
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px'
        }}>
          {['execution', 'psychology', 'lessons'].map((view) => (
            <button
              key={view}
              onClick={() => setAnalysisView(view)}
              style={{
                backgroundColor: analysisView === view ? '#D4AF37' : '#202A44',
                color: analysisView === view ? '#202A44' : '#ffffff',
                border: '1px solid #3a4a6b',
                borderRadius: '4px',
                padding: '12px 20px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: '600',
                textTransform: 'capitalize'
              }}
            >
              {view === 'execution' ? '📊 Execution' : 
               view === 'psychology' ? '🧠 Psychology' : 
               '📚 Lessons'}
            </button>
          ))}
        </div>

        {/* Analysis Content */}
        {renderAnalysisSection()}
      </div>
    </div>
  );
};

export default TradeAnalysis;